import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

import { Icon, IconNames } from 'atoms/Icon';
import fontService, { FontWeight } from 'services/FontService';
import { desktop, tablet } from 'services/ui';

import { authState } from 'core';
import {
  useCourses,
  useFilter,
  useGoToErrorPage,
  useMethodistFilters,
  useMethodistGroupOptions,
} from 'core/hooks';

import {
  COURSES_OPTIONS,
  COURSES_OPTIONS_METHODIST,
  Colors,
  Directions,
  Sizes,
  CoursesFilter,
  UserRole,
} from 'context';

import {
  DropDown,
  Button,
  ButtonSizes,
  Arrow,
  CoursesPreloader,
  FilterPreloader,
} from 'components/atoms';
import { CourseCard, MethodistCoursesFilter } from 'components/molecules';

export const Courses = memo(() => {
  const { t } = useTranslation();
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const isMethodist = active_role === UserRole.METHODIST;

  const { isOpen, selectedOption, toggling, onOptionClicked } = useFilter(active_role as UserRole);
  const {
    search,
    courseId,
    groupId,
    lessonDate,
    setSearch,
    setCourseId,
    setGroupId,
    setLessonDate,
    reset,
    filterVariables,
    hasActiveFilters,
  } = useMethodistFilters();
  const { courseOptions, groupOptions } = useMethodistGroupOptions(isMethodist);

  // Filter changes flow through query variables — Apollo refetches on change,
  // fetchMoreData is only used for after-cursor pagination.
  const {
    data,
    error,
    pageInfo,
    loading,
    operations: { fetchMoreData },
  } = useCourses({
    finished: selectedOption.value === CoursesFilter.finished,
    active: selectedOption.value === CoursesFilter.inProgress,
    ...(isMethodist ? filterVariables : {}),
  });
  const goToErrorPage = useGoToErrorPage();

  const methodistFiltersActive = isMethodist && hasActiveFilters;

  const loadMore = useCallback(() => {
    fetchMoreData(pageInfo?.endCursor);
  }, [fetchMoreData, pageInfo?.endCursor]);

  useEffect(() => {
    if (error) {
      console.log({ coursesError: error });
      goToErrorPage();
    }
  }, [error, goToErrorPage]);

  return (
    <CoursesPageWrapper>
      <DropDownWrapper>
        {isMethodist ? (
          <MethodistCoursesFilter
            search={search}
            onSearchChange={setSearch}
            courseOptions={courseOptions}
            selectedCourseId={courseId}
            onCourseChange={setCourseId}
            groupOptions={groupOptions}
            selectedGroupId={groupId}
            onGroupChange={setGroupId}
            lessonDate={lessonDate}
            onLessonDateChange={setLessonDate}
            onReset={reset}
            completionFilter={
              <DropDown
                options={COURSES_OPTIONS_METHODIST}
                toggling={toggling}
                onOptionClicked={onOptionClicked}
                selectedOption={selectedOption}
                isOpen={isOpen}
              />
            }
          />
        ) : loading ? (
          <FilterPreloader />
        ) : (
          <DropDown
            options={COURSES_OPTIONS}
            toggling={toggling}
            onOptionClicked={onOptionClicked}
            selectedOption={selectedOption}
            isOpen={isOpen}
          />
        )}
      </DropDownWrapper>

      {!loading &&
        Boolean(data.length) &&
        data.map((course) => (
          <CourseCardWrapper key={`${course.id}`}>
            <LinkStyled to={`/courses/${course.id}`}>
              <CourseCard
                key={`${course.id}`}
                id={course.id}
                courseId={course.courseId}
                courseName={course.courseName}
                groupName={course.groupName}
                studentsCount={isDemoUser ? 15 : course.studentsCount}
                courseImageURL={course.courseImageURL}
                activeLessonNumber={course.activeLessonNumber}
                lessonsCount={course.lessonsCount}
                nextLesson={course.nextLesson}
                educationForm={course.educationForm}
                statusFinished={course.statusFinished}
              />
            </LinkStyled>
          </CourseCardWrapper>
        ))}
      {!loading &&
        !data.length &&
        (methodistFiltersActive ? (
          <NoCoursesWrapper>
            <IconStyled name={IconNames.Folder} />
            <TextLarge>{t('methodistFilters.noResults')}</TextLarge>
            <ResetButtonWrapper>
              <Button variant="outline" size={ButtonSizes.md} onClick={reset}>
                {t('methodistFilters.reset')}
              </Button>
            </ResetButtonWrapper>
          </NoCoursesWrapper>
        ) : (
          <NoCoursesWrapper>
            <IconStyled name={IconNames.Folder} />
            <TextLarge>{t('courses.noCourses')}</TextLarge>
            <TextSmall>{t('courses.noCoursesText')}</TextSmall>
          </NoCoursesWrapper>
        ))}

      {loading && <CoursesPreloader />}

      {!loading && pageInfo?.hasNextPage && (
        <ButtonWrapper>
          <Button variant="outline" size={ButtonSizes.md} onClick={() => loadMore()}>
            {t('courses.loadMore')}
            <ArrowWrapper>
              <StyledArrow direction={Directions.down} size={Sizes.md} />
            </ArrowWrapper>
          </Button>
        </ButtonWrapper>
      )}
    </CoursesPageWrapper>
  );
});

const DropDownWrapper = styled.div`
  margin: 32px 0 20px 0;

  ${tablet} {
    margin: 40px 0 20px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const CourseCardWrapper = styled.div`
  margin-bottom: 20px;

  ${desktop} {
    margin-bottom: 32px;
  }
`;

const LinkStyled = styled((props) => <Link {...props} />)`
  text-decoration: none;
  color: ${Colors.black};
`;

const CoursesPageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;
`;

const NoCoursesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 84px;
`;
const IconStyled = styled((props) => <Icon {...props} />)`
  margin-bottom: 32px;
  width: 100px;
  height: 83px;
`;

const TextLarge = styled.p`
  ${fontService.text({ size: 16, lineHeight: 150, weight: FontWeight.bold })};
  color: ${Colors.gray};
  margin-bottom: 8px;

  ${tablet} {
    ${fontService.text({ size: 24, lineHeight: 133, weight: FontWeight.bold })};
  }
`;
const TextSmall = styled.p`
  text-align: center;
  ${fontService.text({ size: 12, lineHeight: 142, weight: FontWeight.regular })};
  color: ${Colors.gray};

  ${tablet} {
    ${fontService.text({ size: 14, lineHeight: 150, weight: FontWeight.regular })};
  }
`;

const StyledArrow = styled(Arrow)``;

const ButtonWrapper = styled.div`
  width: 224px;
  margin: 0 auto;
`;

const ArrowWrapper = styled.div`
  margin: 0 0 5px 15px;
`;

const ResetButtonWrapper = styled.div`
  margin-top: 16px;
  width: 224px;
`;
